/**
 * Social activity (live trades) queries — `/social/trades` powering `/social/activity`,
 * Profile UserActivity, and the chart trader overlay.
 *
 * Two hooks live here:
 *
 *  - `useSocialActivityInfinite` — cursor-paginated history. No polling. Pages
 *    accumulate as the user scrolls.
 *  - `useSocialActivityRecent` — single page of the latest N trades, polling
 *    every `config.pollingInterval` ms. Consumers merge it in front of the
 *    infinite-query pages, deduping by `tradeId`. Splitting recent from history
 *    avoids re-fetching every loaded page on every poll tick.
 *
 * `minAmount`, `seriesTicker`, `nickname` are part of every key — flipping any
 * of them auto-invalidates the cache.
 */

import {
    useInfiniteQuery,
    useQuery,
    type InfiniteData,
    type UseInfiniteQueryOptions,
    type UseInfiniteQueryResult,
    type UseQueryOptions,
    type UseQueryResult,
} from '@tanstack/react-query';

import { createApiClient } from 'lib/api/createApiClient';
import type { SocialActivityItemVo } from 'models';
import type { GetSocialActivityResponse } from 'models/reqres';
import config from 'utils/config';

import { removeEmpty } from './utils';

const api = createApiClient({ baseURL: `${config.exchangeApiUrl}/v1` });

const DEFAULT_PAGE_SIZE = 50;
const RECENT_PAGE_SIZE = 20;

/* -------------------------------------------------------------------------- */
/*                          TYPES                                             */
/* -------------------------------------------------------------------------- */

/**
 * Which `/social/trades` family to call, chosen by the viewer's relationship to
 * the profile — mirrors the mobile app:
 *   - `public`      → `social/trades?nickname=`                    (signed-out / global feed)
 *   - `own`         → `users/{viewerUserId}/social/trades`         (viewing your own profile)
 *   - `profileView` → `users/{viewerUserId}/social/profile_view/trades?nickname=` (signed-in, someone else)
 * `viewerUserId` is always the *signed-in viewer's* id; the target is identified
 * by `nickname` for `profileView`.
 */
export type SocialActivityMode = 'public' | 'own' | 'profileView';

export interface SocialActivityScope {
    mode: SocialActivityMode;
    viewerUserId?: string;
}

export type SocialVisibilityState = 'visible' | 'hidden';

export interface SocialActivityParams {
    minAmount?: number;
    seriesTicker?: string;
    nickname?: string;
    scope?: SocialActivityScope;
}

export interface SocialActivityPage {
    items: SocialActivityItemVo[];
    cursor: string | undefined;
    visibilityState: SocialVisibilityState;
}

/**
 * Pick the endpoint family from the viewer relationship. Falls back to `public`
 * when we lack the viewer's id (e.g. signed-out), so callers can always pass the
 * raw flags without guarding.
 */
export function resolveSocialActivityScope({
    ownProfile,
    isLoggedIn,
    viewerUserId,
}: {
    ownProfile: boolean;
    isLoggedIn: boolean;
    viewerUserId?: string;
}): SocialActivityScope {
    if (ownProfile && viewerUserId) {
        return { mode: 'own', viewerUserId };
    }
    if (isLoggedIn && viewerUserId) {
        return { mode: 'profileView', viewerUserId };
    }
    return { mode: 'public' };
}

/** Fail-open: only the literal `"hidden"` hides the feed (matches mobile). */
export function parseVisibilityState(raw: string | undefined): SocialVisibilityState {
    return raw === 'hidden' ? 'hidden' : 'visible';
}

/** Resolve the request URL + query params for a given scope. */
export function buildSocialActivityRequest(
    params: SocialActivityParams,
    pageSize: number,
    cursor: string | undefined,
): { url: string; queryParams: Record<string, unknown> } {
    const scope = params.scope ?? { mode: 'public' };
    const shared = {
        page_size: pageSize,
        cursor,
        min_amount: params.minAmount,
        series_ticker: params.seriesTicker,
    };

    if (scope.mode === 'own' && scope.viewerUserId) {
        return { url: `users/${scope.viewerUserId}/social/trades`, queryParams: removeEmpty(shared) };
    }
    if (scope.mode === 'profileView' && scope.viewerUserId) {
        return {
            url: `users/${scope.viewerUserId}/social/profile_view/trades`,
            queryParams: removeEmpty({ ...shared, nickname: params.nickname }),
        };
    }
    return { url: 'social/trades', queryParams: removeEmpty({ ...shared, nickname: params.nickname }) };
}

/* -------------------------------------------------------------------------- */
/*                          QUERY KEYS                                        */
/* -------------------------------------------------------------------------- */

export const socialActivityKeys = {
    all: ['socialActivity'] as const,
    infinite: (params: SocialActivityParams, pageSize: number) =>
        [...socialActivityKeys.all, 'infinite', pageSize, removeEmpty(params as Record<string, unknown>)] as const,
    recent: (params: SocialActivityParams) =>
        [...socialActivityKeys.all, 'recent', removeEmpty(params as Record<string, unknown>)] as const,
};

/* -------------------------------------------------------------------------- */
/*                          FETCHERS                                          */
/* -------------------------------------------------------------------------- */

async function fetchSocialActivityPage(
    params: SocialActivityParams,
    pageSize: number,
    cursor: string | undefined,
    signal?: AbortSignal,
): Promise<SocialActivityPage> {
    const { url, queryParams } = buildSocialActivityRequest(params, pageSize, cursor);
    const response = await api.get<GetSocialActivityResponse>(url, {
        params: queryParams,
        signal,
    });
    return {
        items: response.data.trades,
        cursor: response.data.cursor,
        visibilityState: parseVisibilityState(response.data.visibility_state),
    };
}

/* -------------------------------------------------------------------------- */
/*                          INFINITE HISTORY                                  */
/* -------------------------------------------------------------------------- */

type SocialActivityInfiniteOptions = Partial<
    Omit<
        UseInfiniteQueryOptions<
            SocialActivityPage,
            Error,
            InfiniteData<SocialActivityPage, string | undefined>,
            ReturnType<typeof socialActivityKeys.infinite>,
            string | undefined
        >,
        'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
    >
>;

export function useSocialActivityInfinite(
    params: SocialActivityParams,
    pageSize: number = DEFAULT_PAGE_SIZE,
    options?: SocialActivityInfiniteOptions,
): UseInfiniteQueryResult<InfiniteData<SocialActivityPage, string | undefined>, Error> {
    return useInfiniteQuery({
        queryKey: socialActivityKeys.infinite(params, pageSize),
        queryFn: ({ pageParam, signal }) => fetchSocialActivityPage(params, pageSize, pageParam, signal),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.cursor || undefined,
        staleTime: 30 * 1000,
        gcTime: 5 * 60 * 1000,
        retry: 2,
        ...options,
    });
}

/* -------------------------------------------------------------------------- */
/*                          RECENT POLLING                                    */
/* -------------------------------------------------------------------------- */

type SocialActivityRecentOptions = Partial<
    Omit<
        UseQueryOptions<
            SocialActivityItemVo[],
            Error,
            SocialActivityItemVo[],
            ReturnType<typeof socialActivityKeys.recent>
        >,
        'queryKey' | 'queryFn'
    >
>;

/**
 * Latest N trades for a filter combo, refetched on `config.pollingInterval`.
 * Tab-hidden auto-pause is handled by React Query.
 */
export function useSocialActivityRecent(
    params: SocialActivityParams,
    options?: SocialActivityRecentOptions,
): UseQueryResult<SocialActivityItemVo[], Error> {
    return useQuery({
        queryKey: socialActivityKeys.recent(params),
        queryFn: async ({ signal }) => {
            const page = await fetchSocialActivityPage(params, RECENT_PAGE_SIZE, undefined, signal);
            return page.items;
        },
        refetchInterval: config.pollingInterval,
        staleTime: 0,
        gcTime: 60 * 1000,
        retry: 2,
        ...options,
    });
}
